import {
    Component,
    EventEmitter,
    Input,
    OnInit,
    Output,
    inject,
    ChangeDetectionStrategy
} from "@angular/core";
import { FormsModule, ReactiveFormsModule, UntypedFormGroup } from "@angular/forms";
import { IconButtonComponent } from "@components/icon-button/icon-button.component";
import { SelectorComponent } from "@components/selector/selector.component";
import { TreeSelectorComponent } from "@components/tree-selector/tree-selector.component";
import { firstValueFrom } from "rxjs";
import { SelectionItem } from "~app/common/components/tree-selector/selection-item";
import { FormBinderService } from "~app/common/services/form-binder.service";
import { IValidationContext } from "~app/common/validation/IValidationContext";
import { IValidationScope } from "~app/common/validation/IValidationScope";
import { ValidationScopes } from "~app/common/validation/ValidationScopes";
import { IHandelssortimentSearchParamsModel, ISearchHandelssortimentModel } from "~app/features/forsaljning/handelssortiment/handelssortiment.models";
import { HandelssortimentService } from "~app/features/forsaljning/handelssortiment/handelssortiment.service";
import { Befraktarstyrning, IKundDetail } from "../../../models/kundDetail";
import { KundService } from "../../../services/kund.service";
import { KundDetailsModalBefraktarstyrningValidation } from "./kund-details-modal-befraktarstyrning.validation";

@Component({
    selector: "rask-kund-details-modal-befraktarstyrning",
    templateUrl: "./kund-details-modal-befraktarstyrning.component.html",
    styleUrls: ["./kund-details-modal-befraktarstyrning.component.scss"],
    changeDetection: ChangeDetectionStrategy.Eager,
    imports: [
        FormsModule,
        ReactiveFormsModule,
        SelectorComponent,
        TreeSelectorComponent,
        IconButtonComponent
    ]
})
export class KundDetailsModalBefraktarstyrningComponent implements OnInit {
    @Input() data: IKundDetail;
    @Output() deleteRow = new EventEmitter<number>();
    form: UntypedFormGroup;
    handelssortimentSearchParams: IHandelssortimentSearchParamsModel;
    @Input() index: number;
    marknadsomraden: SelectionItem[];
    @Input() rootForm: UntypedFormGroup;
    @Input() styrningRow: Befraktarstyrning;
    validation: IValidationScope;
    validationContext: IValidationContext;

    private readonly formBinder = inject(FormBinderService);
    private readonly handelssortimentService = inject(HandelssortimentService);
    private readonly kundService = inject(KundService);

    delete() {
        this.deleteRow.emit(this.index);
    }

    formatter = (result: ISearchHandelssortimentModel) => result ? result.namn : undefined;

    ngOnInit(): void {
        this.initMarknadsomraden();
        this.form = this.formBinder.CreateGroup(this.styrningRow);
        this.validationContext = KundDetailsModalBefraktarstyrningValidation.init(this.styrningRow, this.data);
        this.validationContext.scope(ValidationScopes.spara).subscribe(v => {
            this.validation = v;
        });

        this.formBinder.attachToRootForm(this.rootForm, this.form, `befraktarstyrning${this.styrningRow.id}`).subscribe(x => {
            this.formBinder.attachValidation(this.validationContext.current, this.form, true);
        });
        this.rootForm.valueChanges.subscribe(() => {
            this.validationContext.validate();
            this.form.updateValueAndValidity({ onlySelf: true,
                emitEvent: false });
        });
    }

    outputFormatter = (result: ISearchHandelssortimentModel) => result ? result.id : undefined;

    searchForHandelssortiment = (searchString: string) => {
        this.handelssortimentSearchParams = {
            aktiv: true,
            skipForadlade: true,
            forProduktion: true
        };
        this.handelssortimentSearchParams.namn = searchString;
        return this.handelssortimentService.searchForHandelssortiment(this.handelssortimentSearchParams);
    };

    selectionChanged() {
        this.styrningRow.marknadsomraden = [];
        this.styrningRow.resultatomraden = [];
        this.styrningRow.produktionsomraden = [];
        this.marknadsomraden.forEach(mo => {
            if (mo.selected) {
                this.styrningRow.marknadsomraden.push(mo.id);
            }
            mo.children.forEach(ro => {
                if (ro.selected) {
                    this.styrningRow.resultatomraden.push(ro.id);
                }
                ro.children.forEach(po => {
                    if (po.selected) {
                        this.styrningRow.produktionsomraden.push(po.id);
                    }
                });
            });
        });
        this.form.markAsDirty();
    }

    private initMarknadsomraden() {
        firstValueFrom(this.kundService.getMarknadsomraden()).then(marknadsomraden => {
            this.marknadsomraden = marknadsomraden.map(mo => ({
                text: mo.namn,
                id: mo.id,
                selected: this.styrningRow.marknadsomraden.includes(mo.id),
                children: mo.children.map(ro => ({
                    text: ro.namn,
                    id: ro.id,
                    selected: this.styrningRow.resultatomraden.includes(ro.id),
                    // eslint-disable-next-line sonarjs/no-nested-functions
                    children: ro.children.map(po => ({
                        text: po.namn,
                        id: po.id,
                        selected: this.styrningRow.produktionsomraden.includes(po.id)
                    } as SelectionItem))
                } as SelectionItem))
            } as SelectionItem));

            this.marknadsomraden.forEach(x => {
                x.children.forEach(y => {
                    if (!x.selected && y.selected) {
                        x.partialSelected = true;
                    }
                    // eslint-disable-next-line sonarjs/no-nested-functions
                    y.children.forEach(z => {
                        if(!y.selected && z.selected) {
                            y.partialSelected = true;
                        }
                    });
                });
            });
        });
    }
}
